/**
 * River water SURFACE — ribbon meshes extruded along the seeded river graph
 * (P1 of the flowing-rivers plan; docs/superpowers/specs/2026-08-02-flowing-
 * rivers-research.md). Until now the carved dry BED was tinted blue in the
 * terrain shader and called a river; this puts an actual water plane at the
 * graph's declared surface elevation `ws`, 0–3.5 m above that bed.
 *
 * Construction samples the connected wet shoreline at each row. Seven cross
 * vertices carry actual depth, with finer rows around falls. The ocean owns
 * submerged mouths; ordinary reaches stop at that handoff.
 *   - U (aCross) = signed fraction across the connected wet channel.
 *   - V (aS) = downstream arclength → the ribbon parameterization IS the flow
 *     map: scrolling V moves everything downstream along the actual course.
 *   - Rivers are split into chunks with chunk-local float32 positions (planet
 *     radii overflow float32 vertex precision) and per-chunk bounding spheres
 *     for frustum culling. Meshes parent to planet.group → orbit + spin free.
 *
 * River surfaces use restrained mineral colour beneath reflected surroundings, with
 * flow-aligned detail that distinguishes quiet pools from steeper reaches. Falls add vertical facets and travelling streaks;
 * whitewater is concentrated in the receiving pool.
 *
 * ⚠⚠ Time: uTime must be a SMALL wrapped clock (float32 ULP at epoch seconds
 * is ~128 s — the phase freezes). All scroll speeds are whole cycle counts per
 * RIVER_WRAP so the wrap itself is seamless for every layer.
 */
import * as THREE from "three";
import {buildWaterfallMist} from './waterfallMist';
import {waterfallFacetTexture} from './waterfallTexture';
import {refineRiverJunctions} from "./riverSurfaceRefinement";
import {
  RIVER_BANK_FACTOR,
  RIVER_CHANNEL_DEPTH_M,
  riverSurfaceRuns,
  fallSheetPoint,
  riverPointBetween,
  type RiverFall,
  type PlanetDef,
  type PlanetSampler,
  type RiverPoint,
  type RiverHit,
  type TerrainSample,
} from "@hyperlight/shared";
import { WATER_UNIFORMS_GLSL } from "./waterMaterial";
import { riverWaterUniforms, RIVER_SHADE_GLSL } from "./riverAppearance";
import { buildFlowClock, flowSeconds, WATER_CHARACTER_GLSL, type FlowClock } from "./surfaceCharacter";

/** Wrap period (s). Every animated layer runs a WHOLE number of cycles per
 *  wrap, so `time % RIVER_WRAP` never pops. */
export const RIVER_WRAP = 600;
const SCROLL1 = 96 / RIVER_WRAP; // falling-water travel time, seamless at wrap
const SCROLL2 = 67 / RIVER_WRAP;

/** Ribbon sits this far above `ws` — keeps the near-tangent waterline contact
 *  from z-shimmering against the carved bank, with margin for coarse terrain
 *  LOD cutting corners above the analytic surface on meander bends. */
export const RIVER_SURFACE_LIFT_M = 0.08;
/** Full nearby reflection, smoothly softened and faded to zero before probe culling. */
export const RIVER_REFLECTION_NEAR_M = 55;
export const RIVER_REFLECTION_FAR_M = 140;
const LIFT_M = RIVER_SURFACE_LIFT_M;
/** Ribbon fades out over this camera-distance band; beyond it the terrain's
 *  painted channel (which stays on) carries the far read for free. */
const FADE_NEAR_M = 1400;
const FADE_FAR_M = 1900;
/** Hide the whole group above this altitude — painted channel + sea sphere own
 *  the orbital view (mirrors the ocean patch's altitude band cull). */
export const RIVER_SHOW_ALT_MAX = 6000;
/** Head fade-in over the first metres of arclength (no hard square source). */
const HEAD_FADE_M = 30;
/** Twice the row density resolves bank headlands. Keep ordinary chunks near
 * their former ~475 m span so that detail does not double the draw calls. */
const CHUNK_PTS = 192;
const LIP_APPROACH_M = 3.5;
/** A single terrain-checked discharge section belongs to both touching meshes. */
interface FallSurface {
  fall:RiverFall;
  rows:RiverPoint[];
  spans:[number,number][];
  lipDepths:number[];
}

export function makeRiverMaterial(def: PlanetDef, detailTex: THREE.Texture): THREE.ShaderMaterial {
  const uniforms = Object.assign(
    THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
    riverWaterUniforms(def.seaColor),
    {
      uTime: { value: 0 },
      uCenter: { value: new THREE.Vector3() }, // planet centre, render space
      uCenterView: { value: new THREE.Vector3() },
      uDetail: { value: detailTex },
      uCurtain: { value: waterfallFacetTexture() },
      uChanDepth: { value: RIVER_CHANNEL_DEPTH_M },
      uScroll1: { value: SCROLL1 },
      uScroll2: { value: SCROLL2 },
      uFadeNear: { value: FADE_NEAR_M },
      uFadeFar: { value: FADE_FAR_M },
      uRiverReflTex: { value: detailTex },
      uRiverReflMatrix: { value: new THREE.Matrix4() },
      uRiverReflViewMatrix: { value: new THREE.Matrix4() },
      uRiverReflViewPlane: { value: new THREE.Vector4() },
      uRiverReflViewAnchor: { value: new THREE.Vector3() },
      uRiverReflPlane: { value: new THREE.Vector4() },
      uRiverReflAnchor: { value: new THREE.Vector3() },
      uRiverReflStrength: { value: 0 },
      uRiverSeaR: { value: def.seaRadius ?? def.radius },
    },
  ) as { [k: string]: THREE.IUniform };

  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: true, // ~opaque interior; early-Z keeps the fill bill honest
    side: THREE.DoubleSide, // swimmers look up through the surface from below
    fog: true,
    uniforms,
    vertexShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_vertex>
      #include <fog_pars_vertex>
      uniform mat4 uRiverReflViewMatrix;
      attribute float aCross;
      attribute float aCrossM;
      attribute float aS;
      attribute float aTravel;
      attribute float aAlpha;
      attribute float aSpeed;
      attribute vec3 aFlow;
      attribute float aFallSlope;
      attribute float aFallTime;
      attribute float aDepth;
      attribute float aCrest;
      varying vec3 vViewPos;
      varying vec4 vReflection;
      varying vec3 vFlowW;
      varying float vCross;
      varying float vCrossM;
      varying float vS;
      varying float vTravel;
      varying float vAlpha;
      varying float vSpeed;
      varying float vFallSlope;
      varying float vFallTime;
      varying float vDepth;
      varying float vCrest;
      void main() {
        vFlowW = normalize((modelMatrix * vec4(aFlow, 0.0)).xyz);
        vCross = aCross;
        vCrossM = aCrossM;
        vS = aS;
        vTravel = aTravel;
        vAlpha = aAlpha;
        vSpeed = aSpeed;
        vFallSlope = aFallSlope;
        vFallTime = aFallTime;
        vDepth = aDepth;
        vCrest = aCrest;
        // modelViewMatrix is composed in CPU double precision. Multiplying
        // viewMatrix by a million-metre float world position loses the bank.
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vViewPos=mv.xyz;
        vReflection=uRiverReflViewMatrix*mv;
        gl_Position = projectionMatrix * mv;
        #ifdef USE_FOG
          vFogDepth = -mv.z;
        #endif
        #include <logdepthbuf_vertex>
      }
    `,
    fragmentShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_fragment>
      #include <fog_pars_fragment>
      ${WATER_UNIFORMS_GLSL}
      ${RIVER_SHADE_GLSL}
      ${WATER_CHARACTER_GLSL}
      uniform float uTime;
      uniform vec3 uCenterView;
      uniform sampler2D uDetail;
      uniform sampler2D uCurtain;
      uniform float uChanDepth;
      uniform float uScroll1;
      uniform float uScroll2;
      uniform float uFadeNear;
      uniform float uFadeFar;
      uniform sampler2D uRiverReflTex;
      uniform vec4 uRiverReflViewPlane;
      uniform vec3 uRiverReflViewAnchor;
      uniform float uRiverReflStrength;
      uniform float uRiverSeaR;
      varying vec3 vViewPos;
      varying vec4 vReflection;
      varying vec3 vFlowW;
      varying float vCross;
      varying float vCrossM;
      varying float vS;
      varying float vTravel;
      varying float vAlpha;
      varying float vSpeed;
      varying float vFallSlope;
      varying float vFallTime;
      varying float vDepth;
      varying float vCrest;
      void main() {
        #include <logdepthbuf_fragment>
        vec3 radialView=vViewPos-uCenterView;
        vec3 up = inverseTransformDirection(normalize(radialView),viewMatrix);
        float depth = max(0.02, vDepth);
        // Absorption follows sampled depth; moving detail cannot recolour an
        // entire shallow bank. Grade and current distinguish pools from rapids.
        float energy=waterEnergy(vFallSlope,vSpeed);
        float footprint=max(fwidth(vTravel)*0.22,fwidth(vCrossM)*0.18);
        float rippleDetail=1.0-smoothstep(0.08,0.5,footprint);
        // The launch row has exactly the same optics as the approaching
        // river. Reflection, ripples and transparency change through the bend,
        // never at a mesh/domain switch.
        float aerated=smoothstep(0.0,0.28,max(0.0,vFallTime));
        vec3 N=normalize(up+vFlowW*max(0.0,vFallSlope));
        vec2 rippleN=vec2(0.0);
        if(rippleDetail>0.004&&aerated<0.999){
          vec3 E=normalize(vFlowW-up*max(0.0,vFallSlope));
          vec3 C=normalize(cross(up,E));
          rippleN=riverRipples(vTravel,vCrossM,uTime,energy,rippleDetail)
            *smoothstep(0.0,0.7,depth)*(1.0-aerated);
          N=normalize(N+E*rippleN.x+C*rippleN.y);
        }
        vec3 V = inverseTransformDirection(normalize(-vViewPos),viewMatrix);
        vec3 sky = uSkyColor;
        #ifdef USE_FOG
          sky = fogColor;
        #endif
        sky = riverSky(sky);
        // One local mirror, only where this surface agrees with its plane.
        // Bends and other elevations fade back to sky rather than reflecting
        // a second, incorrectly placed shoreline across the whole river.
        if (uRiverReflStrength > 0.0 && aerated < 0.999) {
          vec4 rc = vReflection;
          if (rc.w > 0.0) {
            vec2 uv = rc.xy / rc.w + rippleN * 0.14;
            vec2 border = smoothstep(vec2(0.0),vec2(0.06),uv) * smoothstep(vec2(0.0),vec2(0.06),1.0-uv);
            float planeK = 1.0-smoothstep(0.35,2.0,abs(dot(uRiverReflViewPlane,vec4(vViewPos,1.0))));
            float rangeK = 1.0-smoothstep(35.0,100.0,distance(vViewPos,uRiverReflViewAnchor));
            // Preserve reflected silhouettes. Automatic mip selection still
            // handles minification; the former added blur erased the trees.
            vec3 reflected = texture2D(uRiverReflTex,clamp(uv,0.0,1.0)).rgb;
            sky = mix(sky,reflected,uRiverReflStrength*border.x*border.y*planeK*rangeK*(1.0-aerated));
          }
        }
        vec4 w=riverShade(N,V,depth,sky,energy,1.0);
        float lit = 0.16 + 0.84 * uDayLight;
        // Quiet water has no painted streak field or white bank outline.
        // Aeration is confined to shallow, energetic contact and actual falls.
        float contact=waterBreakup(vec2(vS*1.4,vCrossM*2.0),uTime*6.28318530718*(12.0/600.0));
        float shore=1.0-smoothstep(0.0,uFoamWidth,depth);
        float foam=shore*shore*energy*energy*smoothstep(.7,.94,contact)*uFoamStr;
        w.rgb=mix(w.rgb,uFoam*lit,foam*smoothstep(0.0,.3,uDayLight));
        // The SAME travelling tongues cross the upper river and the lip.
        // Travel integrates the river current, then continues in flight time
        // so they accelerate down the sheet without restarting their phase.
        float curtainEdge=1.0;
        if (vCrest > 0.001 || aerated > 0.0) {
          float phase1=(vTravel-uTime)*uScroll1;
          float phase2=(vTravel-uTime)*uScroll2;
          float n1=texture2D(uDetail,vec2(phase1,vCrossM*.18)).r;
          float n2=texture2D(uDetail,vec2(phase2,vCrossM*.073+.41)).r;
          // Clear water rolls over the lip, then separates into pale vertical
          // strands. The old nearly uniform dark panels read as solid rock.
          // Flight time is the material coordinate: strands accelerate with
          // the actual ballistic sheet instead of scrolling down at one speed.
          float strands=smoothstep(.26,.78,n1*.68+n2*.42);
          // Broad folds carry bright, angular planes and cooler recessed
          // water. Fine strands support those forms instead of making a
          // translucent curtain of identical parallel threads.
          vec2 planes=texture2D(uCurtain,vec2(vCrossM*.075,(vTravel-uTime)*.4)).rg;
          float panels=planes.r;
          float fold=.5+.5*sin(vCrossM*2.35+sin(vCrossM*.73)*1.2+vTravel*.7);
          float body=clamp(.40+panels*.55+strands*.07+fold*.10+(1.0-planes.g)*.04,0.0,1.0);
          vec3 shade=uFoam*vec3(.54,.62,.72);
          vec3 faceN=inverseTransformDirection(normalize(cross(dFdx(vViewPos),dFdy(vViewPos))),viewMatrix);
          faceN*=gl_FrontFacing?1.0:-1.0;
          float faceLight=mix(1.0,.84+.16*max(0.0,dot(faceN,uSunDir)),aerated);
          vec3 sheet=mix(shade,uFoam*1.14,body)*lit*faceLight;
          float tongues=vCrest*(.12+.42*strands)*smoothstep(.18,.78,panels);
          w.rgb=mix(w.rgb,sheet,mix(tongues,1.0,aerated));
          w.a=mix(1.0,.86+.11*strands,aerated);
          // Only retreat within the supported sheet: an uneven broken edge
          // cannot push animated water back into the rock or detach the lip.
          float ragged=(.018+.035*panels+.018*strands)*aerated;
          curtainEdge=mix(1.0,1.0-smoothstep(.92-ragged,1.0-ragged,abs(vCross)),aerated);
        }
        float camD = length(vViewPos);
        float distK = 1.0 - smoothstep(uFadeNear, uFadeFar, camD);
        float edge = 1.0-smoothstep(mix(0.98,0.9,aerated),1.0,abs(vCross));
        float alpha = mix(1.0,w.a,aerated) * vAlpha * distK * edge * curtainEdge;
        // Resolve the handoff per pixel. A metre-wide vertex-alpha fade showed
        // the sea through the sloping river as a second, triangular water layer.
        alpha *= smoothstep(${RIVER_SURFACE_LIFT_M + .1},
                            ${RIVER_SURFACE_LIFT_M + .18},
                            length(radialView)-uRiverSeaR);
        if (alpha < 0.004) discard;
        gl_FragColor = vec4(w.rgb, alpha);
        #include <fog_fragment>
      }
    `,
  });
  const centreView=new THREE.Vector3();
  material.onBeforeRender=(_renderer,_scene,camera)=>{
    centreView.copy(uniforms.uCenter.value).applyMatrix4(camera.matrixWorldInverse);
    if(!centreView.equals(uniforms.uCenterView.value)){
      uniforms.uCenterView.value.copy(centreView);
      material.uniformsNeedUpdate=true;
    }
  };
  return material;
}

/**
 * Build every river's ribbon meshes for a planet, parented under one group the
 * caller adds to `planet.group`. Planet-LOCAL coordinates throughout; chunk
 * meshes carry chunk-centre-relative float32 positions for precision.
 */
export function buildRiverRibbonGroup(
  sampler: PlanetSampler,
  def: PlanetDef,
  material: THREE.ShaderMaterial,
): THREE.Group {
  const group = new THREE.Group();
  group.name = "riverRibbons";
  const R = def.radius;
  const A = sampler.amplitude;
  const seaRel = sampler.seaRel;

  const clocks=new Map(sampler.rivers().rivers.map(r=>[r.id,buildFlowClock(r.points)]));
  const fallSurfaces=new Map(sampler.rivers().falls.map(fall=>{
    const rows=Array.from({length:33},(_,i)=>i===0?fall.lip:fallSheetPoint(fall,i/32,A));
    const approach=riverPointBetween(fall.lip,fall.end,-1/(fall.end.s-fall.lip.s));
    const spans=fallingSpans(sampler,rows,R,A,riverWetSpan(sampler,approach,R,A));
    const p=fall.lip,owner=sampler.rivers().carveAt(p.x,p.y,p.z);
    const side=new THREE.Vector3().crossVectors(new THREE.Vector3(owner?.sectionx??p.fx,owner?.sectiony??p.fy,owner?.sectionz??p.fz),new THREE.Vector3(p.x,p.y,p.z)).normalize();
    const sample={}as TerrainSample,surfR=R+p.ws*A+LIFT_M;
    const lipDepths=Array.from({length:9},(_,c)=>{
      const off=spans[0][0]+(spans[0][1]-spans[0][0])*c/8;
      const d=new THREE.Vector3(p.x,p.y,p.z).multiplyScalar(surfR).addScaledVector(side,off).normalize();
      sampler.sample(d.x,d.y,d.z,sample,false);
      return Math.max(0,(p.ws-sample.h)*A);
    });
    return[fall.id,{fall,rows,spans,lipDepths}]as const;
  }));
  const joiningMeshes:THREE.Mesh[]=[];let runKey=0;
  for (const river of sampler.rivers().rivers) {
    for (const run of riverSurfaceRuns(river, sampler.rivers().falls)) {
      if(run.length<2)continue;
      const terminalFall=sampler.rivers().falls.find(f=>f.riverId===river.id&&Math.abs(f.lip.s-run.at(-1)!.s)<.001);
      const approachSurface=terminalFall?fallSurfaces.get(terminalFall.id)!:null;
      const pts:RiverPoint[]=[];let joinsRun=false;runKey++;
      // Keep the normal grid. Only verified joining runs need the bounded
      // error-driven refinement below; wide shoreline fans get extra detail
      // where their actual triangles disagree with the shared surface.
      for(let i=0;i<run.length;i++){
        const a=run[i-1],b=run[i];
        if(a&&sampler.rivers().hasConfluences&&!joinsRun){
          const mid=riverPointBetween(a,b,.5);
          joinsRun=[a,mid,b].some(p=>sampler.rivers().carveAt(p.x,p.y,p.z)?.junctionId!==undefined);
        }
        pts.push(b);
      }
      if (pts.length < 2) continue;
      // The old cutoff was BELOW the graph's elevation floor and never fired.
      // Terminate at the actual sea handoff; no submerged ribbon under the ocean.
      let end = pts.length;
      for (let i = 0; i < pts.length; i++) {
        if ((pts[i].ws - seaRel) * A <= .1) {
          if(i>0){
            const a=pts[i-1],b=pts[i];
            pts[i]=riverPointBetween(a,b,(a.ws-seaRel-.1/A)/(a.ws-b.ws));
          }
          end = i + 1;
          break;
        }
      }
      if (end < 2) continue;
      for (let c0 = 0; c0 < end - 1; c0 += CHUNK_PTS - 1) {
        const c1 = Math.min(c0 + CHUNK_PTS, end);
        const chunk=buildChunk(sampler, pts, c0, c1, R, A, material, clocks.get(river.id)!, clocks,null,approachSurface,joinsRun?runKey:undefined);
        if(joinsRun)joiningMeshes.push(chunk);
        chunk.userData.riverId=river.id;
        if(river.canyonId)chunk.userData.canyonId=river.canyonId;
        group.add(chunk);
        if (c1 >= end) break;
      }
    }
  }
  const refinementSample={}as TerrainSample;
  refineRiverJunctions(joiningMeshes,point=>{
    const d=point.clone().normalize(),hit=sampler.rivers().carveAt(d.x,d.y,d.z);
    if(hit?.junctionId===undefined)return null;
    sampler.sample(d.x,d.y,d.z,refinementSample,false);
    const ws=refinementSample.riverWs??-1e9;
    return ws>-1e8?{radius:R+ws*A+LIFT_M,joining:true,ceiling:refinementSample.h>=ws?R+refinementSample.h*A+((refinementSample.h-ws)*A>LIFT_M?.004:LIFT_M):undefined}:null;
  },vertex=>{
    const d=vertex.point.clone().normalize(),hit=sampler.rivers().carveAt(d.x,d.y,d.z);
    sampler.sample(d.x,d.y,d.z,refinementSample,false);
    const sample=refinementSample,ws=sample.riverWs??-1e9,a=vertex.attributes;
    if(ws>-1e8)vertex.point.copy(d).multiplyScalar(R+ws*A+LIFT_M);
    a.aAlpha[0]=sample.town<=.25&&sample.road>=0&&ws>-1e8?a.aAlpha[0]:0;
    a.aSpeed[0]=(sample.riverSpeed??0)*sample.river;
    if(hit){a.aS[0]=hit.s;a.aTravel[0]=flowSeconds(clocks.get(hit.riverId)!,hit.s);a.aFlow=[hit.fx,hit.fy,hit.fz];a.aFallSlope[0]=hit.slope*A;}
    a.aDepth[0]=ws>-1e8?Math.max(0,(ws-sample.h)*A):0;
  });
  for (const fall of sampler.rivers().falls) {
    const surface=fallSurfaces.get(fall.id)!,rows=surface.rows;
    const mesh = buildChunk(sampler, rows, 0, rows.length, R, A, material, clocks.get(fall.riverId)!, clocks, surface);
    mesh.name = `waterfall:${fall.id}`;
    group.add(mesh);
    const impact=fallSheetPoint(fall,1,A),sample={} as TerrainSample;
    sampler.sample(impact.x,impact.y,impact.z,sample,false);
    // Effects require an actual receiving water column after settlement
    // carving too; a dry road cannot keep the old pool's spray and foam.
    if(sample.town<=.25&&sample.road>=0&&(sample.riverWs??-1e9)>sample.h
      &&Math.abs((sample.riverWs??-1e9)-impact.ws)*A<.1){
      const mist=buildWaterfallMist(sampler,fall,R,A,material,riverWetSpan(sampler,impact,R,A));
      if(mist)group.add(mist);
      const foam=buildImpactFoam(sampler, fall, R, A, material);
      if(foam)group.add(foam);
    }
  }
  return group;
}

/** Broken foam at the plunge, confined to the receiving pool rather than the curtain. */
function buildImpactFoam(sampler: PlanetSampler, fall: RiverFall, R: number, A: number, water: THREE.ShaderMaterial): THREE.Mesh|null {
  const p=fallSheetPoint(fall,1,A);
  const up=new THREE.Vector3(p.x,p.y,p.z),flow=new THREE.Vector3(p.fx,p.fy,p.fz);
  const side=new THREE.Vector3().crossVectors(flow,up).normalize();
  const center=up.clone().multiplyScalar(R+p.ws*A+LIFT_M+.035);
  const span=riverWetSpan(sampler,p,R,A),width=(span[1]-span[0])*.96;
  if(width<.1)return null;
  const length=Math.min(14,Math.max(6,fall.dropM*.28+width*.45));
  const columns=13,rows=17,positions:number[]=[],uvs:number[]=[],depths:number[]=[],kept:number[]=[];
  const sample={} as TerrainSample;
  const at=(across:number,along:number)=>{
    const d=center.clone().addScaledVector(side,across).addScaledVector(flow,along).normalize();
    sampler.sample(d.x,d.y,d.z,sample,false);
    const ws=sample.riverWs??-1e9;
    const wet=sample.town<=.25&&sample.road>=0&&ws>sample.h&&(ws-p.ws)*A<.15;
    return {point:d.multiplyScalar(R+(wet?ws:p.ws)*A+LIFT_M+.035).sub(center),
      wet,depth:wet?Math.max(0,(ws-sample.h)*A):0};
  };
  const acrossAt=(u:number)=>(span[0]+span[1])*.5+(u-.5)*width;
  const alongAt=(v:number)=>-.35+v*(length+.35);
  const supported:boolean[]=[];
  for(let row=0;row<rows;row++)for(let col=0;col<columns;col++){
    const u=col/(columns-1),v=row/(rows-1),value=at(acrossAt(u),alongAt(v));
    positions.push(...value.point.toArray());uvs.push(u,v);depths.push(value.depth);supported.push(value.wet);
  }
  const keep=(a:number,b:number,c:number)=>{
    if(!supported[a]||!supported[b]||!supported[c])return;
    // Corners alone can bridge a dry shelf. Check every edge and the centre;
    // the apron follows the final receiving water even as it bends/downsteps.
    const probes=[[a,b],[b,c],[c,a],[a,b,c]];
    if(probes.every(ids=>at(acrossAt(ids.reduce((n,i)=>n+uvs[i*2],0)/ids.length),
      alongAt(ids.reduce((n,i)=>n+uvs[i*2+1],0)/ids.length)).wet))kept.push(a,b,c);
  };
  for(let row=0;row<rows-1;row++)for(let col=0;col<columns-1;col++){
    const a=row*columns+col,b=a+1,c=a+columns,d=c+1;keep(a,b,c);keep(b,d,c);
  }
  if(!kept.length)return null;
  const geo=new THREE.BufferGeometry();
  geo.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));
  geo.setAttribute('uv',new THREE.Float32BufferAttribute(uvs,2));
  geo.setAttribute('aDepth',new THREE.Float32BufferAttribute(depths,1));
  geo.setIndex(kept);geo.computeBoundingSphere();
  const material=new THREE.ShaderMaterial({transparent:true,depthWrite:false,fog:true,
    uniforms:{...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),uTime:water.uniforms.uTime,uDayLight:water.uniforms.uDayLight,uFoam:water.uniforms.uFoam,
      uFadeNear:water.uniforms.uFadeNear,uFadeFar:water.uniforms.uFadeFar,
      uWidth:{value:width},uLength:{value:length}},
    vertexShader:`
      #include <common>
      #include <logdepthbuf_pars_vertex>
      #include <fog_pars_vertex>
      varying vec2 vUv; varying float vFade; varying float vDepth; attribute float aDepth;
      uniform float uFadeNear; uniform float uFadeFar;
      void main(){
        vUv=uv;vDepth=aDepth;
        vec4 mvPosition=modelViewMatrix*vec4(position,1.0);
        vFade=1.0-smoothstep(uFadeNear,uFadeFar,length(mvPosition.xyz));
        gl_Position=projectionMatrix*mvPosition;
        #include <fog_vertex>
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader:`
      #include <common>
      #include <logdepthbuf_pars_fragment>
      #include <fog_pars_fragment>
      varying vec2 vUv; varying float vFade; varying float vDepth;
      uniform float uTime; uniform float uDayLight; uniform vec3 uFoam;
      uniform float uWidth; uniform float uLength;
      float foamHash(vec2 p){return fract(sin(dot(mod(p,vec2(90.0,480.0)),vec2(127.1,311.7)))*43758.5453);}
      // Linear triangular interpolation makes broken angular foam veins,
      // rather than round isolated decals or concentric targets.
      float foamField(vec2 p){
        p.x+=p.y*.375;
        vec2 i=floor(p),f=fract(p);
        float a=foamHash(i),b=foamHash(i+vec2(1,0)),c=foamHash(i+vec2(0,1)),d=foamHash(i+1.0);
        return f.x+f.y<1.0?a+(b-a)*f.x+(c-a)*f.y:d+(c-d)*(1.0-f.x)+(b-d)*(1.0-f.y);
      }
      void main(){
        #include <logdepthbuf_fragment>
        float across=(vUv.x-.5)*uWidth,along=vUv.y*(uLength+.35)-.35;
        vec2 p=vec2(across*1.15+sin(along*.7)*.3,along*.90-uTime*.8);
        float field=foamField(p);
        float veins=1.0-smoothstep(.045,.145,abs(field-.51));
        float flecks=smoothstep(.63,.81,foamField(p+vec2(3.7,11.0)));
        float core=exp(-max(0.0,along)*.36);
        float lateral=1.0-smoothstep(.55,1.0,abs(vUv.x*2.0-1.0));
        float downstream=1.0-smoothstep(.40,1.0,vUv.y);
        float lip=smoothstep(-.35,.12,along);
        float froth=smoothstep(.38,.65,field);
        float coverage=mix(veins*.28+froth*.48+flecks*.16,.72+field*.25,core*.80);
        float alpha=coverage*lateral*downstream*lip*smoothstep(0.0,.18,vDepth)*vFade;
        if(alpha<.01)discard;
        gl_FragColor=vec4(uFoam*(.16+.84*uDayLight),alpha);
        #include <fog_fragment>
      }`});
  const mesh=new THREE.Mesh(geo,material);mesh.position.copy(center);
  mesh.name=`waterfall-foam:${fall.id}`;mesh.renderOrder=4;
  return mesh;
}

function buildChunk(
  sampler: PlanetSampler,
  pts: readonly RiverPoint[],
  i0: number,
  i1: number,
  R: number,
  A: number,
  material: THREE.ShaderMaterial,
  clock: FlowClock,
  clocks: ReadonlyMap<number,FlowClock>,
  fallSurface:FallSurface|null=null,
  approachSurface:FallSurface|null=null,
  runKey?:number,
): THREE.Mesh {
  const fallSpeed=fallSurface?.fall.lip.speed??0;
  const n = i1 - i0;
  const sample={} as TerrainSample;
  const rowOwners=Array.from({length:n},(_,i)=>{const p=pts[i0+i];return sampler.rivers().carveAt(p.x,p.y,p.z);});
  // Only the few rows at a lip need the curtain's nine columns. A zipper
  // strip joins seven to nine without new meshes or T-junctions. Row layout
  // depends on its absolute river position, so chunk edges agree as well.
  const rowColumns=Array.from({length:n},(_,i)=>fallSurface||approachSurface&&approachSurface.fall.lip.s-pts[i0+i].s<LIP_APPROACH_M?9:7);
  let vertexCount=0;
  const rowStarts=rowColumns.map(columns=>{const first=vertexCount;vertexCount+=columns;return first;});
  const positions = new Float32Array(vertexCount * 3);
  const aCross = new Float32Array(vertexCount);
  const aCrossM = new Float32Array(vertexCount);
  const aS = new Float32Array(vertexCount);
  const aTravel = new Float32Array(vertexCount);
  const aAlpha = new Float32Array(vertexCount);
  const aSpeed = new Float32Array(vertexCount);
  const aFlow = new Float32Array(vertexCount * 3);
  const aFallSlope = new Float32Array(vertexCount);
  const aFallTime = new Float32Array(vertexCount);
  const aDepth = new Float32Array(vertexCount);
  const aCrest = new Float32Array(vertexCount);
  const lipFall=fallSurface?.fall;
  const launch=fallSurface??approachSurface;

  // Chunk centre: the middle point's surface position (planet-local).
  const mid = pts[i0 + ((n / 2) | 0)];
  const midR = R + mid.ws * A + LIFT_M;
  const cx = mid.x * midR;
  const cy = mid.y * midR;
  const cz = mid.z * midR;

  for (let i = 0; i < n; i++) {
    const NC=rowColumns[i];
    const p = pts[i0 + i];
    const owner=rowOwners[i];
    const lipDistance=launch?launch.fall.lip.s-p.s:Infinity;
    const atLip=!!launch&&Math.abs(lipDistance)<.001;
    const crest=fallSurface?1:Math.pow(Math.max(0,1-Math.max(0,lipDistance)/LIP_APPROACH_M),1.4);
    const surfR = R + p.ws * A + LIFT_M;
    // Cross direction: flow × radial (both unit, near-perpendicular).
    const fx=owner?.sectionx??p.fx,fy=owner?.sectiony??p.fy,fz=owner?.sectionz??p.fz;
    let sx = fy * p.z - fz * p.y;
    let sy = fz * p.x - fx * p.z;
    let sz = fx * p.y - fy * p.x;
    const sl = Math.hypot(sx, sy, sz) || 1;
    sx /= sl;
    sy /= sl;
    sz /= sl;
    const wetSpan=fallSurface?null:riverWetSpan(sampler,p,R,A,owner??undefined);
    let span=fallSurface?.spans[i]??wetSpan!;
    if(approachSurface&&crest>0){
      const lipSpan=approachSurface.spans[0];
      // Narrow progressively into the validated discharge, never widening
      // over a dry approach bank. At the lip BOTH surfaces own this section.
      span=atLip?lipSpan:[Math.max(span[0],span[0]+(lipSpan[0]-span[0])*crest),Math.min(span[1],span[1]+(lipSpan[1]-span[1])*crest)];
    }
    // Fade the spring only; the fragment shader resolves the ocean waterline.
    const headK = Math.min(1, p.s / HEAD_FADE_M);
    const alpha = headK;
    for (let c = 0; c < NC; c++) {
      const crossFrac = (c / (NC - 1)) * 2 - 1; // −1 .. +1
      const off = span[0]+(span[1]-span[0])*c/(NC-1);
      // Offset along the chord, then re-seat on the ws sphere (the offset drops
      // ~w²/2R below it otherwise — 0.2 m on a wide lowland river).
      let px = p.x * surfR + sx * off;
      let py = p.y * surfR + sy * off;
      let pz = p.z * surfR + sz * off;
      if(lipFall&&i>0&&i<n-1&&c>0&&c<NC-1){
        // Shallow downstream folds give the sheet actual angular relief.
        // They keep the ballistic height and taper to zero at every physical
        // connection. Each displacement is checked against the final ground.
        const t=i/(n-1),fold=.5+.5*Math.sin(off*2.1+Math.sin(off*.73)+t*7.0);
        const bulge=Math.min(.32,lipFall.dropM*.012)*Math.sin(Math.PI*t)*Math.sin(Math.PI*c/(NC-1))*(.25+.75*fold);
        const bx=px+p.fx*bulge,by=py+p.fy*bulge,bz=pz+p.fz*bulge,l=Math.hypot(bx,by,bz);
        sampler.sample(bx/l,by/l,bz/l,sample,false);
        if(sample.town<=.25&&sample.road>=0&&(p.ws-sample.h)*A+LIFT_M>.08){px=bx;py=by;pz=bz;}
      }
      const pl = surfR / Math.hypot(px, py, pz);
      px *= pl;
      py *= pl;
      pz *= pl;
      const dx=px/surfR,dy=py/surfR,dz=pz/surfR;
      const hit=sampler.rivers().carveAt(dx,dy,dz);
      sampler.sample(dx,dy,dz,sample,false);
      // Every standing vertex follows final local water, including another
      // segment of the same bend. Row-centre elevation is only authoritative
      // for the deliberately airborne sheet and its exact upper lip.
      const suspended=fallSpeed>0||atLip;
      const claimed=(sample.riverWs??-1e9)>-1e8;
      const ws=suspended||!claimed?p.ws:sample.riverWs!;
      const vertexR=R+ws*A+LIFT_M;
      px=dx*vertexR;py=dy*vertexR;pz=dz*vertexR;
      const groundR = R + sample.h*A;
      const dropM = R + ws * A - groundR;
      const vi = rowStarts[i] + c;
      positions[vi * 3] = px - cx;
      positions[vi * 3 + 1] = py - cy;
      positions[vi * 3 + 2] = pz - cz;
      aCross[vi] = crossFrac;
      aCrossM[vi] = off;
      aS[vi] = !suspended&&hit ? hit.s : p.s;
      const flight=fallSurface?(p.s-fallSurface.fall.lip.s)/fallSpeed:0;
      aTravel[vi] = fallSurface?flowSeconds(clock,fallSurface.fall.lip.s)+flight:atLip?flowSeconds(clock,launch!.fall.lip.s):flowSeconds(!suspended&&hit ? clocks.get(hit.riverId) ?? clock : clock,aS[vi]);
      aAlpha[vi] = sample.town<=.25&&sample.road>=0&&(fallSpeed>0||atLip||(sample.riverWs??-1e9)>-1e8) ? alpha : 0;
      aFallSlope[vi] = atLip?0:fallSpeed > 0 ? Math.sqrt(Math.max(0,p.speed*p.speed-fallSpeed*fallSpeed))/fallSpeed : (hit?.slope??0)*A*(1-crest);
      aFallTime[vi] = fallSurface?flight:launch?-Math.min(1,Math.max(0,lipDistance)/launch.fall.lip.speed):-1;
      const depthBlend=Math.min(1,flight/.28);
      aDepth[vi] = fallSurface?fallSurface.lipDepths[c]*(1-depthBlend)+2*depthBlend:atLip?launch!.lipDepths[c]:Math.max(0, dropM);
      aCrest[vi] = crest;
      // The shared current weight slows every bank and side pool continuously.
      // A pool endpoint must not switch the shader back to nominal speed.
      aSpeed[vi] = fallSpeed>0||atLip ? p.speed : (sample.riverSpeed??0)*sample.river;
      aFlow[vi * 3] = fallSpeed>0||atLip?p.fx:hit?.fx??p.fx;
      aFlow[vi * 3 + 1] = fallSpeed>0||atLip?p.fy:hit?.fy??p.fy;
      aFlow[vi * 3 + 2] = fallSpeed>0||atLip?p.fz:hit?.fz??p.fz;
    }
  }

  // Winding: front faces must point OUT of the planet (up). With rows ordered
  // downstream and columns along side = flow × radial, the (r0, r1, r0+1)
  // order wound the normals INWARD — the surface was backface-culled from
  // every above-water view and only visible from underwater.
  const triangleCount=rowColumns.slice(1).reduce((sum,columns,i)=>sum+rowColumns[i]+columns-2,0);
  const indices = new Uint32Array(triangleCount * 3);
  let o = 0;
  for (let i = 0; i < n - 1; i++) {
    const r0=rowStarts[i],r1=rowStarts[i+1],n0=rowColumns[i]-1,n1=rowColumns[i+1]-1;
    let a=0,b=0;
    while(a<n0||b<n1){
      const nextA=a<n0?(a+1)*n1:Infinity,nextB=b<n1?(b+1)*n0:Infinity;
      if(nextA<=nextB){indices[o++]=r0+a;indices[o++]=r0+a+1;indices[o++]=r1+b;a++;}
      if(nextB<=nextA){indices[o++]=r0+a;indices[o++]=r1+b+1;indices[o++]=r1+b;b++;}
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("aCross", new THREE.BufferAttribute(aCross, 1));
  geo.setAttribute("aCrossM", new THREE.BufferAttribute(aCrossM, 1));
  geo.setAttribute("aS", new THREE.BufferAttribute(aS, 1));
  geo.setAttribute("aTravel", new THREE.BufferAttribute(aTravel, 1));
  geo.setAttribute("aAlpha", new THREE.BufferAttribute(aAlpha, 1));
  geo.setAttribute("aSpeed", new THREE.BufferAttribute(aSpeed, 1));
  geo.setAttribute("aFlow", new THREE.BufferAttribute(aFlow, 3));
  geo.setAttribute("aFallSlope", new THREE.BufferAttribute(aFallSlope, 1));
  geo.setAttribute("aFallTime", new THREE.BufferAttribute(aFallTime, 1));
  geo.setAttribute("aDepth", new THREE.BufferAttribute(aDepth, 1));
  geo.setAttribute("aCrest", new THREE.BufferAttribute(aCrest, 1));
  geo.setIndex(new THREE.BufferAttribute(indices, 1));
  if(runKey!==undefined)geo.userData.riverVertexKeys=rowColumns.flatMap((columns,i)=>Array.from({length:columns},(_,c)=>`${runKey}:${i0+i}:${c}`));
  geo.computeBoundingSphere();

  const mesh = new THREE.Mesh(geo, material);
  mesh.position.set(cx, cy, cz);
  mesh.renderOrder = 3; // with the transparent water family, over terrain
  return mesh;
}

/** A falling curtain can narrow past a projecting bank. Reuse the lip's
 * discharge width, then retain only its connected clear interval at each row.
 * Construction only; no terrain queries or moving mesh buffers per frame. */
function fallingSpans(sampler:PlanetSampler,rows:RiverPoint[],R:number,A:number,lipSpan:[number,number]):[number,number][]{
  const sample={} as TerrainSample;
  const spanAt=(p:RiverPoint):[number,number]=>{
    const hit=sampler.rivers().carveAt(p.x,p.y,p.z);
    const fx=hit?.sectionx??p.fx,fy=hit?.sectiony??p.fy,fz=hit?.sectionz??p.fz;
    const sx=fy*p.z-fz*p.y,sy=fz*p.x-fx*p.z,sz=fx*p.y-fy*p.x;
    const l=Math.hypot(sx,sy,sz)||1,surface=R+p.ws*A+LIFT_M;
    const clear=(off:number)=>{
      const x=p.x+sx/l*off/surface,y=p.y+sy/l*off/surface,z=p.z+sz/l*off/surface,n=Math.hypot(x,y,z);
      sampler.sample(x/n,y/n,z/n,sample,false);
      return sample.town<=.25&&sample.road>=0&&(p.ws-sample.h)*A+LIFT_M>.04;
    };
    if(!clear(0))return[0,0];
    return lipSpan.map(limit=>{
      let lo=0;
      for(let j=1;j<=8;j++){
        let hi=limit*j/8;
        if(!clear(hi)){
          for(let k=0;k<9;k++){const mid=(lo+hi)*.5;if(clear(mid))lo=mid;else hi=mid;}
          return lo;
        }
        lo=hi;
      }
      return limit;
    }) as [number,number];
  };
  const spans=rows.map(spanAt);
  // Also constrain the space between rows, where a triangle otherwise cuts
  // across a projecting bank although both of its endpoint rows are clear.
  for(let i=1;i<rows.length;i++){
    const middle=spanAt(riverPointBetween(rows[i-1],rows[i],.5));
    for(const j of[i-1,i]){spans[j][0]=Math.max(spans[j][0],middle[0]);spans[j][1]=Math.min(spans[j][1],middle[1]);}
  }
  return spans;
}

/** Connected wet cross-section. Stop at the first dry bank on either side;
 * never span over an island or leave a rectangular apron hanging off a bend. */
export function riverWetSpan(sampler: PlanetSampler,p: RiverPoint,R: number,A: number,owner?:RiverHit): [number,number] {
  const sample={} as TerrainSample;
  const hit=owner??sampler.rivers().carveAt(p.x,p.y,p.z);
  const fx=hit?.sectionx??p.fx,fy=hit?.sectiony??p.fy,fz=hit?.sectionz??p.fz;
  let sx=fy*p.z-fz*p.y,sy=fz*p.x-fx*p.z,sz=fx*p.y-fy*p.x;
  const sl=Math.hypot(sx,sy,sz)||1;sx/=sl;sy/=sl;sz/=sl;
  const wet=(off:number)=>{
    const x=p.x+sx*off/R,y=p.y+sy*off/R,z=p.z+sz*off/R,l=Math.hypot(x,y,z);
    sampler.sample(x/l,y/l,z/l,sample,false);
    // Final shared ownership excludes settled ground and unsupported pool
    // shoulders. Elevation alone would invent a floating rectangular apron.
    const ws=sample.riverWs??-1e9;
    // Standing vertices follow this same local water level. A centre-row
    // elevation cannot describe the bank on both sides of a curved reach.
    return ws>-1e8&&sample.town<=.25&&sample.road>=0&&(ws-sample.h)*A>0;
  };
  if(!wet(0))return [0,0];
  const limit=hit?.claimHalf??p.half*RIVER_BANK_FACTOR;
  const edge=(side:number)=>{
    let lo=0;
    for(let i=1;i<=12;i++){
      let hi=limit*i/12;
      if(!wet(hi*side)){
        for(let j=0;j<6;j++){const mid=(lo+hi)*.5;if(wet(mid*side))lo=mid;else hi=mid;}
        return (lo+hi)*.5*side;
      }
      lo=hi;
    }
    return limit*side;
  };
  return [edge(-1),edge(1)];
}
